import PropTypes from 'prop-types';

import {
  useClickstreamCallback,
  useModalDisplayedEvent
} from '@zenbusiness/application-commons-clickstream';
import SharedAsset from '@zenbusiness/zui/SharedAsset';

import AuthModal from './AuthModal';
import * as FormStyled from './AuthModal.form.style';
import { CLICKSTREAM_COMMON_PROPS } from '../utils/constants';

/**
 * Upgrade Modal Component
 * Promotes features and encourages anonymous users to create an account or log in
 *
 * @param {object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {Function} props.onClose - Callback when modal closes
 * @param {Function} props.onCreateAccount - Callback to open create account modal
 * @param {Function} props.onLogin - Callback to open login modal
 * @returns {object} The UpgradeModal component
 */
function UpgradeModal({ isOpen, onClose, onCreateAccount, onLogin }) {
  const trackButtonPressed = useClickstreamCallback('buttonPressed');

  // Track modal display
  useModalDisplayedEvent({
    open: isOpen,
    elementName: 'upgrade_modal'
  });

  const handleCreateAccount = () => {
    trackButtonPressed({
      ...CLICKSTREAM_COMMON_PROPS,
      elementName: 'upgrade_modal_create_account',
      elementText: 'Create account'
    });
    onCreateAccount();
  };

  const handleLogin = () => {
    trackButtonPressed({
      ...CLICKSTREAM_COMMON_PROPS,
      elementName: 'upgrade_modal_login',
      elementText: 'Log in'
    });
    onLogin();
  };

  const handleCancel = () => {
    trackButtonPressed({
      ...CLICKSTREAM_COMMON_PROPS,
      elementName: 'upgrade_modal_cancel',
      elementText: 'Cancel'
    });
    onClose();
  };

  return (
    <AuthModal
      ariaLabel="Get the most out of Velo"
      isOpen={isOpen}
      maxWidth="50rem"
      onClose={onClose}
    >
      <FormStyled.FormStack>
        {/* Title Section */}
        <FormStyled.TitleContainer>
          <FormStyled.Title size="xl" weight="bold">
            Get the most out of Velo
          </FormStyled.Title>
          <FormStyled.Subtitle>
            Create an account to see saved threads and get personalized insights. Already have an
            account? <FormStyled.SwitchLink onClick={handleLogin}>Log in</FormStyled.SwitchLink>
          </FormStyled.Subtitle>
        </FormStyled.TitleContainer>

        <SharedAsset
          alt="Velo features preview"
          src="/s2/raster/velo-create-account.png"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
        />

        {/* Action Buttons */}
        <FormStyled.ButtonStack direction="row">
          <FormStyled.SubmitButton
            clickstreamProps={{
              ...CLICKSTREAM_COMMON_PROPS,
              elementName: 'upgrade_modal_create_account_button'
            }}
            onClick={handleCreateAccount}
          >
            Create account
          </FormStyled.SubmitButton>

          <FormStyled.SecondaryButton
            clickstreamProps={{
              ...CLICKSTREAM_COMMON_PROPS,
              elementName: 'upgrade_modal_cancel_button'
            }}
            variant="outlined"
            onClick={handleCancel}
          >
            Cancel
          </FormStyled.SecondaryButton>
        </FormStyled.ButtonStack>
      </FormStyled.FormStack>
    </AuthModal>
  );
}

UpgradeModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreateAccount: PropTypes.func.isRequired,
  onLogin: PropTypes.func.isRequired
};

export default UpgradeModal;
